import { z } from "zod";
import type { Operation } from "../types";
import { idNumericParams } from "./_common";

export const stageKind = z.enum([
  "lead",
  "active",
  "offer",
  "hired",
  "rejected",
]);
export type StageKind = z.infer<typeof stageKind>;

export const stageSchema = z.object({
  id: z.number(),
  pipeline_id: z.number(),
  slug: z.string(),
  name: z.string(),
  position: z.number(),
  kind: stageKind,
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type StageShape = z.infer<typeof stageSchema>;

export const pipelineSchema = z.object({
  id: z.number(),
  workspace_id: z.number(),
  slug: z.string(),
  name: z.string(),
  is_default: z.number(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  stages: z.array(stageSchema),
});
export type PipelineShape = z.infer<typeof pipelineSchema>;

export const pipelinesList: Operation<void, void, void, PipelineShape[]> = {
  method: "GET",
  path: () => "/hiring/api/pipelines",
  response: z.array(pipelineSchema),
};

export const pipelineCreateBody = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  is_default: z.boolean().optional(),
});

// coverage-ignore: mutate ops deferred on Hono (hiring.ts NOTE); list is mounted
export const pipelinesCreate: Operation<
  void,
  void,
  z.infer<typeof pipelineCreateBody>,
  PipelineShape
> = {
  method: "POST",
  path: () => "/hiring/api/pipelines",
  body: pipelineCreateBody,
  response: pipelineSchema,
  // list embeds stages; any pipeline/stage write stales the collection.
  invalidates: [pipelinesList],
};

export const stageAddBody = z.object({
  slug: z.string().min(1),
  name: z.string().min(1),
  position: z.number().int().optional(),
  kind: stageKind,
});

// coverage-ignore: mutate ops deferred on Hono (hiring.ts NOTE); list is mounted
export const stagesAdd: Operation<
  { id: number },
  void,
  z.infer<typeof stageAddBody>,
  StageShape
> = {
  method: "POST",
  path: ({ id }) => `/hiring/api/pipelines/${id}/stages`,
  params: idNumericParams,
  body: stageAddBody,
  response: stageSchema,
  invalidates: [pipelinesList],
};

export const stageUpdateBody = z.object({
  name: z.string().optional(),
  position: z.number().int().optional(),
  slug: z.string().optional(),
});

// coverage-ignore: mutate ops deferred on Hono (hiring.ts NOTE); list is mounted
export const stagesUpdate: Operation<
  { id: number; stageId: number },
  void,
  z.infer<typeof stageUpdateBody>,
  StageShape
> = {
  method: "PATCH",
  path: ({ id, stageId }) => `/hiring/api/pipelines/${id}/stages/${stageId}`,
  params: z.object({
    id: z.coerce.number().int(),
    stageId: z.coerce.number().int(),
  }),
  body: stageUpdateBody,
  response: stageSchema,
  invalidates: [pipelinesList],
};

// coverage-ignore: mutate ops deferred on Hono (hiring.ts NOTE); list is mounted
export const stagesArchive: Operation<
  { id: number; stageId: number },
  void,
  void,
  { id: number }
> = {
  method: "DELETE",
  path: ({ id, stageId }) => `/hiring/api/pipelines/${id}/stages/${stageId}`,
  params: z.object({
    id: z.coerce.number().int(),
    stageId: z.coerce.number().int(),
  }),
  response: z.object({ id: z.number() }),
  invalidates: [pipelinesList],
};

export const hiringPipelines = {
  list: pipelinesList,
  create: pipelinesCreate,
  stagesAdd,
  stagesUpdate,
  stagesArchive,
};
